import React from 'react'
import Link from 'next/link'
import footerLinksRaw from './footerLinks.json'
import { FaFacebookF, FaXTwitter, FaYoutube, FaLinkedinIn, FaGithub } from 'react-icons/fa6'
import { FooterLinksData, FooterProps } from './Footer.interface'
import styles from './Footer.module.scss'

const isExternalHref = (href: string) => href.startsWith('http://') || href.startsWith('https://')

const { cloudInsightsLinks, resourcesLinks } = footerLinksRaw as FooterLinksData

const socialLinks = [
  { Icon: FaFacebookF, href: 'https://www.facebook.com/zscaler', label: 'Facebook' },
  { Icon: FaXTwitter, href: 'https://twitter.com/zscaler', label: 'X' },
  { Icon: FaYoutube, href: 'https://www.youtube.com/user/ZscalerMarketing', label: 'YouTube' },
  { Icon: FaLinkedinIn, href: 'https://www.linkedin.com/company/zscaler', label: 'LinkedIn' },
  { Icon: FaGithub, href: 'https://github.com/zscaler', label: 'GitHub' },
]

const Footer: React.FC<FooterProps> = () => {
  const currentYear = new Date().getFullYear()

  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        {/* ABOUT THREATLABZ SECTION */}
        <div className={styles.aboutSection}>
          <h3 className={`mb-[20px] ${styles.heading}`}>ABOUT THREATLABZ</h3>
          <p className={`mb-[30px] ${styles.description}`}>
            ThreatLabz is the embedded research team at Zscaler. This global team includes security
            experts, researchers, and network engineers responsible for analyzing and eliminating
            threats across the Zscaler security cloud and investigating the global threat landscape.
            The team shares its research and cloud data with the industry at large to help promote a
            safer internet.
          </p>
          <div className={styles.socials}>
            {socialLinks.map(({ Icon, href, label }) => (
              <Link
                href={href}
                key={label}
                aria-label={label}
                className={styles.socialIcon}
                {...(isExternalHref(href)
                  ? { target: '_blank' as const, rel: 'noopener noreferrer' }
                  : {})}
              >
                <Icon />
              </Link>
            ))}
          </div>
          <p className={styles.copyright}>
            ©{' '}
            <Link
              className={styles.link}
              href={'https://www.zscaler.com/'}
              target="_blank"
              rel="noopener noreferrer"
            >
              Zscaler Inc
            </Link>
            , 2008-{currentYear}. All rights reserved.
          </p>
        </div>
        <div className={`${styles.linksWrapper}  flex flex-row`}>
          {/* CLOUD INSIGHTS DASHBOARD SECTION */}
          <div className={`${styles.linksColumn}`}>
            <h3 className={`mb-[20px] ${styles.heading}`}>CLOUD INSIGHTS DASHBOARD</h3>
            <ul className={styles.linkList}>
              {cloudInsightsLinks.map((link) => (
                <li key={link.text}>
                  <Link
                    href={link.href}
                    className={styles.link}
                    {...(isExternalHref(link.href)
                      ? { target: '_blank' as const, rel: 'noopener noreferrer' }
                      : {})}
                  >
                    {link.text}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* RESOURCES SECTION */}
          <div className={`${styles.linksColumn}`}>
            <h3 className={styles.heading}>RESOURCES</h3>
            <ul className={styles.linkList}>
              {resourcesLinks.map((link) => (
                <li key={link.text} className={link.mobileOnly ? styles.mobileOnly : ''}>
                  <Link
                    href={link.href}
                    className={styles.link}
                    {...(isExternalHref(link.href)
                      ? { target: '_blank' as const, rel: 'noopener noreferrer' }
                      : {})}
                  >
                    {link.text}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
